// Pure rekenfuncties voor de Payroll-module (module D1).
//
// Géén berekeningen in routes of componenten — alle datum-, week- en
// bedrag-allocatie voor maandelijkse payroll-exports komt hier samen. Stijl
// volgt sick-leave-calculations / uren-calculations: alle datums via
// UTC-midnight om DST/TZ-issues te vermijden.
//
// Conventies:
//   - ISO-week 8601: maandag is dag 1, week 1 bevat de eerste donderdag.
//   - Targetmaand: 1-12, 1-based (januari=1).
//   - Bedragen als plain `number`; afronding op cent (2 decimalen) door caller.

import { getWeekDateRange, getIsoWeek } from './uren-calculations'

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/** Format Date naar ISO yyyy-MM-dd (intern gebruik voor padding). */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

// ────────────────────────────────────────────────────────────────────────────
// Week → maand allocatie
// ────────────────────────────────────────────────────────────────────────────

/**
 * Geeft alle datums (Date) binnen targetMonth/targetYear die in de ISO-week
 * (weekYear, weekNumber) vallen. Voor een week die over een maand-grens loopt
 * zal dit een subset van de 7 weekdagen returnen; voor een week volledig
 * binnen de maand zijn dit alle 7 dagen; voor een week volledig buiten de
 * maand een lege array.
 *
 * Returnt UTC-midnight Dates.
 *
 * Bron: D1-spec — week-naar-maand-allocatie voor weekstaat-aggregatie.
 */
export function allocateDaysToMonth(
  weekYear: number,
  weekNumber: number,
  targetYear: number,
  targetMonth: number,
): Date[] {
  if (
    !Number.isFinite(weekYear) ||
    !Number.isFinite(weekNumber) ||
    !Number.isFinite(targetYear) ||
    !Number.isFinite(targetMonth) ||
    targetMonth < 1 ||
    targetMonth > 12
  ) {
    return []
  }

  const { start } = getWeekDateRange(weekYear, weekNumber)
  const out: Date[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(start)
    d.setUTCDate(start.getUTCDate() + i)
    // getUTCMonth is 0-based; targetMonth is 1-based.
    if (d.getUTCFullYear() === targetYear && d.getUTCMonth() + 1 === targetMonth) {
      out.push(d)
    }
  }
  return out
}

// ────────────────────────────────────────────────────────────────────────────
// Bedrag-berekeningen
// ────────────────────────────────────────────────────────────────────────────

/**
 * Bereken bruto totaal voor een PayrollExportRegel uit losse componenten.
 *
 * Som = brutoUren-bedrag + overuren-bedrag + ploegentoeslag + declaraties
 *       + verlof-uitbetaling.
 *
 * Note: brutoUren wordt hier als bedrag (€) verwacht, niet als uren — caller
 * berekent uren × tarief vóór het aanroepen. Dit houdt de helper stateless.
 *
 * FO-411 (batch-54) — de KILOMETERVERGOEDING (`reisVergoeding`) is HIER WEG en
 * telt dus NIET meer in het bruto. De afspraak is: reisUREN worden verloond
 * (tijd × tarief, zit via `brutoUren` in de som), kilometers niet — die zijn
 * klantfacturatie (doorbelast op de factuur), geen looncomponent; monteurs
 * rijden in bedrijfsbussen. Het parameter-veld is bewust VERWIJDERD in plaats
 * van op 0 gezet: elke caller die 'm nog meegaf krijgt nu een compile-fout
 * (excess-property-check) en moet expliciet kiezen — dat is de vangrail die
 * voorkomt dat er stil weer loon bij komt.
 *
 * De uitzondering (medewerker met eigen vervoer, `Employee.eigenVervoerKm-
 * Vergoeding`) loopt via {@link berekenNettoVergoedingTotaal}: een aparte
 * NETTO-onkostencomponent met eigen looncode, nooit in het bruto.
 *
 * Negatieve correcties zijn toegestaan (admin-override op een regel kan een
 * negatief bedrag opleveren bij correctie van een eerdere export).
 *
 * Returnt afgerond op 2 decimalen.
 */
export function berekenBrutoTotaal(input: {
  brutoUren: number
  overurenBedrag: number
  ploegtoeslagBedrag: number
  declaratiesTotaal: number
  verlofUitbetaling: number
  /**
   * Extra 100%-toeslag voor "werken op verjaardag als feestdag" (spec §6.3).
   * Het normale 100%-loon zit al in `brutoUren`; dit veld is uitsluitend de
   * extra 100% bovenop. Optional zodat bestaande callers (en tests die geen
   * verjaardag-data hebben) onveranderd doorlopen.
   */
  verjaardagToeslagBedrag?: number
  /**
   * FO-389 (batch-46) — consignatievergoeding (standby): aantal piketweken x
   * verloningstarief. Additief looncomponent die in `brutoTotaal` optelt (zoals
   * `ploegtoeslagBedrag`). Optional zodat bestaande callers ongewijzigd doorlopen.
   * LET OP: de oproep-UREN (`consignatieOproepUren`) zitten al in `brutoUren` en
   * zijn GEEN aparte brutoTotaal-component (informatief label, patroon `verlofUren`).
   */
  consignatieVergoeding?: number
}): number {
  const sum =
    (input.brutoUren ?? 0) +
    (input.overurenBedrag ?? 0) +
    (input.ploegtoeslagBedrag ?? 0) +
    (input.declaratiesTotaal ?? 0) +
    (input.verlofUitbetaling ?? 0) +
    (input.verjaardagToeslagBedrag ?? 0) +
    (input.consignatieVergoeding ?? 0)
  return Math.round(sum * 100) / 100
}

/**
 * FO-411 (batch-54) — totaal van de NETTO-onkostencomponenten van één
 * payroll-regel. Staat bewust NAAST {@link berekenBrutoTotaal}: netto-
 * vergoedingen zijn geen loon en mogen nooit in het bruto-totaal belanden,
 * maar moeten wél apart optelbaar en reconcilieerbaar zijn (de payroll-
 * som-guard reconcilieert bruto en netto los van elkaar).
 *
 * `kmVergoedingNetto` = de kilometervergoeding van een medewerker MET de
 * regeling "eigen vervoer - km-vergoeding uitbetalen" (`Employee.eigenVervoer-
 * KmVergoeding`), per leg-datum bepaald vanaf de ingangsdatum. Zonder die
 * regeling is de waarde 0 en verschijnt er geen netto-regel in de export.
 *
 * Returnt afgerond op 2 decimalen.
 */
export function berekenNettoVergoedingTotaal(input: {
  kmVergoedingNetto?: number
}): number {
  const sum = input.kmVergoedingNetto ?? 0
  return Math.round(sum * 100) / 100
}

/**
 * FO-411 (batch-54) — telt de km-vergoeding van deze leg-datum als NETTO
 * uitbetaalbare onkosten? Eén gedeelde regel voor de aggregator (payroll) en
 * de tests, zodat "per leg-datum, niet met terugwerkende kracht over de hele
 * maand" (spec §6.2) nergens anders geïnterpreteerd wordt.
 *
 * Regels:
 *   - vlag uit            → nooit (default: km is facturatie, geen loon);
 *   - vlag aan, geen datum → vanaf altijd (de regeling geldt onbeperkt terug);
 *   - vlag aan + datum     → alleen legs OP of NA de ingangsdatum.
 *
 * Datums worden als kalenderdag (`yyyy-MM-dd`) vergeleken, niet als instant:
 * een leg hoort bij een dag, niet bij een tijdstip. Beide argumenten zijn
 * daarom dag-sleutels; de caller leidt die af uit de reis-dagsleutel
 * (`ReisUrenBerekening.datum`) resp. de ingangsdatum.
 */
export function kmVergoedingTeltNetto(input: {
  eigenVervoerKmVergoeding: boolean
  /** Ingangsdatum als `yyyy-MM-dd`; null/leeg = geen ingangsdatum (altijd). */
  ingangsdatumYmd: string | null
  /** Leg-datum als `yyyy-MM-dd`. */
  legDatumYmd: string
}): boolean {
  if (!input.eigenVervoerKmVergoeding) return false
  const vanaf = input.ingangsdatumYmd
  if (!vanaf) return true
  return input.legDatumYmd >= vanaf
}

/**
 * Bereken overuren-bedrag op basis van uren × tarief × factor.
 *
 * Default-factor 1.5 = CAO Metaal en Techniek standaard voor doordeweekse
 * overuren (100% basis + 50% premie). Bij "tijd voor tijd" (`tvtWerkurenEnabled`)
 * wordt de 100%-basis als TVT-tijd gecompenseerd en alleen de premie in geld;
 * de caller geeft dan factor 0.5 mee. Zie docs/specs/tvt-overwerk-premie-only.md.
 * Andere factoren (zaterdag/zondag/feestdag) zijn niet in scope voor D1.
 *
 * Negatieve/niet-eindige uren of factor return 0 (defensieve guard — overuren-
 * correctie loopt via een aparte handmatige regel, niet via deze helper).
 *
 * Returnt afgerond op 2 decimalen.
 */
export function berekenOverurenBedrag(
  overuren: number,
  uurtarief: number,
  factor = 1.5,
): number {
  if (!Number.isFinite(overuren) || !Number.isFinite(uurtarief) || !Number.isFinite(factor)) return 0
  if (overuren <= 0 || uurtarief <= 0 || factor <= 0) return 0
  const bedrag = overuren * uurtarief * factor
  return Math.round(bedrag * 100) / 100
}

/**
 * FO-122 — TVT-opbouwfactor (0 / 0,5 / 1) voor één TVT-soort (werk of reis).
 *
 * Eén gedeelde bron voor de saldo-opbouw (uren.ts) én de payroll-geld-verdeling
 * (payroll.ts), zodat tijd-kant en geld-kant nooit uiteenlopen:
 *   - `enabled` uit → 0 (geen opbouw).
 *   - `atvEnabled` uit → 0 (CAO-invariant: TVT bouwt alleen op mét ATV-opbouw).
 *   - percentage 50 → 0,5 (helft naar TVT-tijd, helft uitbetaald).
 *   - anders (100, default, of onverwacht) → 1 (volledige opbouw = bestaand gedrag).
 *
 * `percentage` mag een Decimal-afgeleide `number` zijn (caller doet `Number(...)`).
 */
export function tvtFactor(
  enabled: boolean,
  atvEnabled: boolean,
  percentage: number | null | undefined,
): number {
  if (!enabled || !atvEnabled) return 0
  // `Number(...)` defensief: een caller die de Decimal rauw doorgeeft (i.p.v.
  // `Number(decimal)`) krijgt zo nog steeds factor 0,5 bij 50 i.p.v. stil 1.
  return Number(percentage) === 50 ? 0.5 : 1
}

/**
 * FO-122 — de geld-factor van een overuur bij een TVT-factor: 1,5 - werkFactor
 * (TVT uit: 1,5x vol uitbetaald; half: 1,0x; volledig: 0,5x, alleen de toeslag).
 * Eén bron voor de verloning (`routes/payroll.ts`) en de doorrekening van de
 * vakantiecampagne (batch-70, FO-525 §C12); niet dupliceren.
 */
export function overuurGeldFactor(werkFactor: number): number {
  return 1.5 - werkFactor
}

/**
 * FO-122 — het uitbetaalde deel van een reisuur bij een TVT-factor: 1 - reisFactor
 * (TVT uit: vol; half: de helft; volledig: niets). Kilometervergoeding staat los.
 */
export function reisGeldFactor(reisFactor: number): number {
  return 1 - reisFactor
}

// ────────────────────────────────────────────────────────────────────────────
// FO-389 (batch-46) — consignatie/standby-weken per exportmaand
// ────────────────────────────────────────────────────────────────────────────

/** Eén piketweek-rooster-rij (bron: `ConsignatieRooster`) voor de teller. */
export interface ConsignatieRoosterWeek {
  employeeId: string
  /**
   * De maandag (`ConsignatieRooster.startDate`) van de piketweek. Dit is de
   * autoritatieve bron van "welke ISO-week" (canoniek afgeleid uit weekNr bij
   * aanmaak); de teller leidt de donderdag + het canonieke weeknummer hieruit af,
   * ONAFHANKELIJK van hoe `weekNr` als string is opgeslagen (gepad of ongepad).
   */
  startDate: Date
  clientId: string
  /** Klantnaam voor de preview-reconciliatie (niet nodig voor de telling zelf). */
  clientNaam?: string | null
}

/** Eén getelde piketweek van één medewerker (dedupe per (employeeId, weekNr)). */
export interface ConsignatieWeekAllocatie {
  weekNr: string
  /** Alle klanten waarvoor de monteur die week piket had (uniek, gesorteerd). */
  clientIds: string[]
  clientNamen: string[]
  /**
   * Dubbele bezetting: de monteur stond die ISO-week voor > 1 klant ingepland.
   * Beschikbaarheid is één keer beschikbaar, dus dit levert één vergoeding + een
   * signaal in de preview (§3.3, sluit aan op `GET /consignatie/dekking`).
   */
  dubbeleBezetting: boolean
}

/** Telling per medewerker over één exportmaand. */
export interface ConsignatieMaandTelling {
  /** Aantal getelde piketweken (distinct (employeeId, weekNr) met donderdag in de maand). */
  weken: number
  /** Per getelde week de klant(en) + het dubbele-bezetting-signaal (gesorteerd op weekNr). */
  allocaties: ConsignatieWeekAllocatie[]
  /** True zodra minstens één week dubbele bezetting had (preview-signaal). */
  heeftDubbeleBezetting: boolean
}

/**
 * Parse ISO-weeknotatie "YYYY-Www" naar {year, week}; null bij een ongeldige
 * waarde. Tolerant voor het gepad-of-ongepad-weeknummer ("2026-W5" én "2026-W05")
 * omdat de bron-validatie (`weekNrSchema`) beide toestaat.
 */
export function parseIsoWeekNr(weekNr: string): { year: number; week: number } | null {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(weekNr)
  if (!m) return null
  const year = Number(m[1])
  const week = Number(m[2])
  if (!Number.isFinite(year) || !Number.isFinite(week) || week < 1 || week > 53) return null
  return { year, week }
}

/** Canoniek, gepad weeknummer ("YYYY-Www") van de ISO-week waar `startDate` in valt. */
function canoniekWeekNr(startDate: Date): string {
  const { year, week } = getIsoWeek(startDate)
  return `${year}-W${pad2(week)}`
}

/**
 * FO-389 (batch-46) §3.1/§3.3/§3.5 — tel de piketweken per medewerker die in de
 * gegeven exportmaand vallen, volgens de payroll-week-conventie: **een week telt
 * in de maand van zijn donderdag**. Zo valt elke ISO-week in precies één export
 * (geen halve weken over de maandgrens, geen dubbeltelling).
 *
 * De donderdag + het canonieke weeknummer worden afgeleid uit de rooster-`startDate`
 * (de canoniek berekende maandag), NIET uit de `weekNr`-string — die kan gepad of
 * ongepad zijn ("2026-W5"/"2026-W05") en zou anders een week stil laten wegvallen.
 *
 * PURE functie: de caller filtert de bron al (`deletedAt: null`, status !=
 * GEANNULEERD) en levert alleen te-tellen rooster-rijen aan.
 *
 * Dedupe op (employeeId, canoniek weeknummer): heeft een monteur die week piket
 * voor twee klanten (uniek is per klant), dan telt de week één keer — beschikbaarheid
 * is één keer beschikbaar. De dubbele bezetting wordt als signaal teruggegeven.
 *
 * Returnt een Map per employeeId; medewerkers zonder piketweek in deze maand
 * komen niet in de Map voor.
 */
export function telConsignatieWekenVoorMaand(
  roosters: ConsignatieRoosterWeek[],
  targetYear: number,
  targetMonth: number,
): Map<string, ConsignatieMaandTelling> {
  // Stap 1 — dedupe per (employeeId, canoniek weekNr), verzamel de klanten en
  // filter op de donderdag-maand. `Map<employeeId, Map<weekNr, {clientIds, clientNamen}>>`.
  const perEmp = new Map<string, Map<string, { ids: Set<string>; namen: Map<string, string> }>>()

  for (const r of roosters) {
    // Donderdag van de ISO-week = maandag (startDate) + 3 dagen; die maand bepaalt
    // de export. startDate is UTC-middernacht van de maandag (canoniek afgeleid).
    const donderdag = new Date(r.startDate)
    donderdag.setUTCDate(donderdag.getUTCDate() + 3)
    if (donderdag.getUTCFullYear() !== targetYear || donderdag.getUTCMonth() + 1 !== targetMonth) {
      continue
    }
    const weekNr = canoniekWeekNr(r.startDate)

    let weken = perEmp.get(r.employeeId)
    if (!weken) {
      weken = new Map()
      perEmp.set(r.employeeId, weken)
    }
    let bucket = weken.get(weekNr)
    if (!bucket) {
      bucket = { ids: new Set(), namen: new Map() }
      weken.set(weekNr, bucket)
    }
    bucket.ids.add(r.clientId)
    if (r.clientNaam) bucket.namen.set(r.clientId, r.clientNaam)
  }

  // Stap 2 — bouw de per-medewerker-telling met gesorteerde allocaties.
  const out = new Map<string, ConsignatieMaandTelling>()
  for (const [employeeId, weken] of perEmp) {
    const allocaties: ConsignatieWeekAllocatie[] = [...weken.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekNr, bucket]) => {
        const clientIds = [...bucket.ids].sort()
        return {
          weekNr,
          clientIds,
          clientNamen: clientIds.map((id) => bucket.namen.get(id) ?? id),
          dubbeleBezetting: clientIds.length > 1,
        }
      })
    out.set(employeeId, {
      weken: allocaties.length,
      allocaties,
      heeftDubbeleBezetting: allocaties.some((a) => a.dubbeleBezetting),
    })
  }
  return out
}

// ────────────────────────────────────────────────────────────────────────────
// Reisuren-verrekening tegen de contracturen (FO-007)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Verdeel de uitbetaalde reisuren over twee zichtbare posten t.o.v. de
 * contracturen-norm van de periode (FO-007):
 *
 *   - `verrekendeReisUren` — reisuren die het tekort tussen werkuren en
 *     contracturen aanvullen (ingezet om de contracturen te halen);
 *   - `overigeReisUren`     — de reisuren die daarna overblijven.
 *
 * Regels (FO-007 §6):
 *   verrekendeReisUren = max(0, min(reisuren, contracturen − werkuren))
 *   overigeReisUren     = reisuren − verrekendeReisUren
 *
 * Dit is een PURE, AFGELEIDE weergave: het verandert het uit te betalen bedrag
 * niet. De reisuren worden hoe dan ook beloond; de verrekening verklaart alleen
 * hoe de contracturen worden gehaald (eerst werkuren, dan reisuren). De som van
 * de twee posten is per definitie gelijk aan de uitbetaalde reisuren, zodat het
 * overzicht nooit afwijkt van wat verloond wordt.
 *
 * Afronding op 2 decimalen, consistent met de rest van de payroll-aggregatie.
 * De inputs worden eerst afgerond en `overigeReisUren` wordt als verschil
 * berekend, zodat `verrekendeReisUren + overigeReisUren === reisuren` exact
 * blijft (geen afrondingsgat tussen de posten).
 *
 * Randgevallen:
 *   - werkuren >= contracturen → geen tekort → verrekend 0, alles overig.
 *   - contracturen <= 0 (oproep-/nul-urencontract, of geen contractmutatie op
 *     peildatum) → niets te verrekenen → verrekend 0, alles overig.
 *   - niet-eindige of negatieve invoer → defensief naar 0.
 */
export function berekenReisurenVerrekening(input: {
  werkuren: number
  reisuren: number
  contracturen: number
}): { verrekendeReisUren: number; overigeReisUren: number } {
  const werkuren = roundUren(input.werkuren)
  const reisuren = roundUren(input.reisuren)
  const contracturen = roundUren(input.contracturen)

  if (reisuren <= 0 || contracturen <= 0) {
    return { verrekendeReisUren: 0, overigeReisUren: Math.max(0, reisuren) }
  }

  const tekort = roundUren(Math.max(0, contracturen - werkuren))
  const verrekendeReisUren = roundUren(Math.min(reisuren, tekort))
  const overigeReisUren = roundUren(reisuren - verrekendeReisUren)
  return { verrekendeReisUren, overigeReisUren }
}

/** Afronding op urenniveau (2 decimalen); niet-eindige invoer → 0. */
function roundUren(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

// ────────────────────────────────────────────────────────────────────────────
// String-helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Format jaar + maand naar "YYYY-MM" string voor file-naming en lookups.
 * Maand wordt gepad met leading zero (1 → "01").
 */
export function maandKey(year: number, month: number): string {
  return `${year}-${pad2(month)}`
}
